import datapoints from './datapoints';
import markItemsAsPinnedService from '../services/markItemsAsPinnedService';
import { setRowOperation } from 'owa-mail-list-actions';
import { actionNames, MarkConversationsAsPinned } from 'owa-mail-store-actions';
import * as undoActions from 'owa-mail-undo';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import {
    filterConversationRowsByRestriction,
    getIsTableConversationRelationPinned,
    getTableConversationRelation,
} from 'owa-mail-list-store';
import {
    getRowIdsFromRowKeys,
    RowOperation,
    TableView,
    TableViewConversationRelation,
} from 'owa-mail-list-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { userMailInteractionAction } from 'owa-mail-actions';

function getItemIdsToUpdate(
    rowKeys: string[],
    tableViewId: string,
    shouldMarkAsPinned: boolean
): string[] {
    const itemIdsToUpdate: { [id: string]: boolean } = {};

    for (let i = 0; i < rowKeys.length; i++) {
        const tableConversationRelation = getTableConversationRelation(rowKeys[i], tableViewId);

        if (shouldMarkAsPinned) {
            // If pinning, pin the last item in each conversation. The last delivered item is first in the list of ItemIds
            const itemId = tableConversationRelation.itemIds[0];
            if (!itemIdsToUpdate[itemId]) {
                itemIdsToUpdate[itemId] = true;
            }
        } else {
            // If unpinning, unpin all local items in conversation.
            tableConversationRelation.itemIds.forEach(itemId => {
                if (!itemIdsToUpdate[itemId]) {
                    itemIdsToUpdate[itemId] = true;
                }
            });
        }
    }

    return Object.keys(itemIdsToUpdate);
}

function shouldPerformPinUnpinAction(
    tableConversationRelation: TableViewConversationRelation,
    pin: boolean
): boolean {
    return (
        tableConversationRelation &&
        getIsTableConversationRelationPinned(tableConversationRelation) != pin
    );
}

export default createLegacyActionWithDatapoint('pinConversations', datapoints.pinConversations)(
    function pinConversations(
        conversationRowKeys: string[],
        tableView: TableView,
        shouldMarkAsPinned: boolean,
        actionSource: ActionSource
    ): Promise<any> {
        undoActions.clearLastUndoableAction();

        const filteredRowKeys = filterConversationRowsByRestriction(
            conversationRowKeys,
            tableView.id,
            shouldPerformPinUnpinAction,
            shouldMarkAsPinned
        );

        const conversationIdStrings = getRowIdsFromRowKeys(filteredRowKeys, tableView.id);
        const itemIdsToUpdate = getItemIdsToUpdate(
            filteredRowKeys,
            tableView.id,
            shouldMarkAsPinned
        );

        if (itemIdsToUpdate.length == 0) {
            // No need to send out the service call
            return Promise.resolve();
        }

        raiseAction<MarkConversationsAsPinned>(actionNames.MarkConversationsAsPinned)(
            conversationIdStrings,
            tableView.id,
            shouldMarkAsPinned
        );

        // Treat this pinning operation as a row move operation
        setRowOperation(tableView, RowOperation.RowMove);

        if (shouldMarkAsPinned) {
            const instrumentationContexts = getInstrumentationContextsFromTableView(
                conversationRowKeys,
                tableView
            );
            userMailInteractionAction('Pin', instrumentationContexts);
        }

        // Make service request
        // Task 658: Reload client state when pin action fails on the server
        return markItemsAsPinnedService(itemIdsToUpdate, shouldMarkAsPinned);
    }
);
